import { Trans } from '@lingui/react/macro';
import { QRCodeSVG } from 'qrcode.react';

import config from '@/config';
import Dialog from '@/design_system/Dialog';
import Paragraph from '@/design_system/Typography/Paragraph';
import { CreateMediumBody } from '@/models/medium';
import { useCreateSessionLimitedToken } from '@/models/session';
import { createBEMClasses } from '@/utils/classname';
import { cleanObjectFromUndefinedValues } from '@/utils/object';

import './QrCodeDialog.css';

const { block, element } = createBEMClasses('qr-code-dialog');

type QrCodeDialogProps = {
  url: string;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
};

export const useCreateQrCodeUrl = () => {
  const { mutateAsync: createLimitedTokenSession, isPending: isCreateSessionLimitedTokenPending } =
    useCreateSessionLimitedToken();

  const createQrCodeUrl = async (
    uploadData: CreateMediumBody,
    uploadType: string,
    maxNumberOfMedia?: number
  ) => {
    const baseUrl = `${config.appUrl}/mobile-upload`;

    const { limitedToken } = await createLimitedTokenSession();
    const cleanedUploadData = cleanObjectFromUndefinedValues({
      ...uploadData,
      uploadType,
      maxNumberOfMedia,
    });
    const params = new URLSearchParams(cleanedUploadData);

    params.set('limitedToken', limitedToken);

    return `${baseUrl}?${params.toString()}`;
  };

  return { createQrCodeUrl, isCreateSessionLimitedTokenPending };
};

const QrCodeDialog = ({ url, setIsOpen, isOpen }: QrCodeDialogProps) => {
  return (
    <Dialog
      title={<Trans id="design-system.qr-code.dialog-title">Upload from a phone</Trans>}
      isOpen={isOpen}
      onOpenChange={(isOpen: boolean) => {
        setIsOpen(isOpen);
      }}
      style={{ width: '400px' }}
      className={block()}
    >
      <main>
        <Paragraph>
          <Trans id="design-system.qr-code.dialog-label">
            Use your phone camera to scan the QR code and upload files directly from your request.
          </Trans>
        </Paragraph>
        <QRCodeSVG className={element('qr-code')} size={198} title={url ?? ''} value={url ?? ''} />
      </main>
    </Dialog>
  );
};

export default QrCodeDialog;
